import AddCircleOutlineRoundedIcon from '@mui/icons-material/AddCircleOutlineRounded';
import HistoryRoundedIcon from '@mui/icons-material/HistoryRounded';
import TuneRoundedIcon from '@mui/icons-material/TuneRounded';
import { Box, ButtonBase, Grid, Paper, Stack, Typography } from '@mui/material';
import React from 'react';

export type ConfigurationSource = 'blank' | 'preset' | 'previousJob';

interface StartFromStepProps {
  value: ConfigurationSource | null;
  onChange: (source: ConfigurationSource) => void;
  presetContent: React.ReactNode;
  previousJobContent: React.ReactNode;
  previousJobsAvailable: boolean;
  checkingPreviousJobs: boolean;
}

const options: Array<{
  value: ConfigurationSource;
  title: string;
  description: string;
  icon: React.ReactElement;
}> = [
  {
    value: 'blank',
    title: 'Start blank',
    description: 'Build a new configuration from the available inputs.',
    icon: <AddCircleOutlineRoundedIcon />,
  },
  {
    value: 'preset',
    title: 'Load preset',
    description: 'Use a saved calculate-features configuration as your starting point.',
    icon: <TuneRoundedIcon />,
  },
  {
    value: 'previousJob',
    title: 'Reuse previous job',
    description: 'Restore settings and input selections from an earlier job.',
    icon: <HistoryRoundedIcon />,
  },
];

export const StartFromStep: React.FC<StartFromStepProps> = ({
  value,
  onChange,
  presetContent,
  previousJobContent,
  previousJobsAvailable,
  checkingPreviousJobs,
}) => (
  <Stack spacing={3}>
    <Box>
      <Typography variant="h6" gutterBottom>
        How would you like to start?
      </Typography>
      <Typography variant="body2" color="text.secondary">
        Choose a source first. It will guide the configuration steps that follow.
      </Typography>
    </Box>
    <Grid container spacing={2}>
      {options.map((option) => {
        const selected = value === option.value;
        const disabled = option.value === 'previousJob' && !checkingPreviousJobs && !previousJobsAvailable;
        return (
          <Grid item xs={12} md={4} key={option.value}>
            <Paper
              variant="outlined"
              sx={{
                height: '100%',
                borderColor: selected ? 'primary.main' : 'divider',
                bgcolor: selected ? 'action.selected' : 'background.paper',
              }}
            >
              <ButtonBase
                disabled={disabled}
                onClick={() => onChange(option.value)}
                aria-pressed={selected}
                sx={{
                  width: '100%',
                  height: '100%',
                  minHeight: 132,
                  p: 2,
                  alignItems: 'flex-start',
                  justifyContent: 'flex-start',
                  textAlign: 'left',
                  opacity: disabled ? 0.5 : 1,
                }}
              >
                <Stack spacing={1}>
                  <Box sx={{ color: selected ? 'primary.main' : 'text.secondary', display: 'flex' }}>{option.icon}</Box>
                  <Typography variant="subtitle1" fontWeight={600}>
                    {option.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {option.value === 'previousJob' && disabled
                      ? 'No previous jobs are available.'
                      : option.value === 'previousJob' && checkingPreviousJobs
                      ? 'Checking for previous jobs…'
                      : option.description}
                  </Typography>
                </Stack>
              </ButtonBase>
            </Paper>
          </Grid>
        );
      })}
    </Grid>
    {value === 'preset' && <Box>{presetContent}</Box>}
    {value === 'previousJob' && <Box>{previousJobContent}</Box>}
  </Stack>
);
